// Comprehensive Inmate Database Service
import { supabase } from '../lib/supabase';

class InmateService {
  // Enhanced search with improved "John" search functionality
  flexibleSearch = async (searchParams) => {
    try {
      // Enhanced search parameters - handle single name searches like "John"
      let enhancedParams = { ...searchParams };
      
      // If only one search term is provided, search both first and last name
      if (searchParams?.query && !searchParams?.firstName && !searchParams?.lastName) {
        const searchTerm = searchParams?.query?.trim();
        enhancedParams = {
          ...searchParams,
          firstName: searchTerm,
          lastName: searchTerm,
          fuzzy: true // Enable fuzzy matching for single term searches
        };
      }

      // FIX: Correct parameter names to match database function
      const { data, error } = await supabase?.rpc('search_flex', {
        p_first: enhancedParams?.firstName || null,
        p_last: enhancedParams?.lastName || null,
        p_dob: enhancedParams?.dob || null,
        p_facility: enhancedParams?.facility || null,
        p_agency: enhancedParams?.agency || null,
        p_status: enhancedParams?.status || null,
        p_arrest_date_from: enhancedParams?.arrestDateFrom || null,
        p_arrest_date_to: enhancedParams?.arrestDateTo || null,
        p_release_date_from: enhancedParams?.releaseDateFrom || null,
        p_release_date_to: enhancedParams?.releaseDateTo || null,
        p_charge_q: enhancedParams?.chargeQuery || null,
        p_warrant_q: enhancedParams?.warrantQuery || null,
        p_city: enhancedParams?.city || null,
        p_zip: enhancedParams?.zip || null,
        p_booking_no: enhancedParams?.bookingNumber || null,
        p_case_no: enhancedParams?.caseNumber || null,
        p_fuzzy: enhancedParams?.fuzzy || false,
        p_page: enhancedParams?.page || 1,
        p_page_size: enhancedParams?.pageSize || 25
      });

      if (error) {
        console.error('🚨 Search error:', error);
        // Check if user has exceeded search limits
        if (error?.message?.includes('search limit') || error?.message?.includes('quota exceeded')) {
          // Redirect to search limit exhaustion URL
          window.location.href = 'https://www.sacsheriff.com/InmateInformation/SearchNames.aspx';
          return {
            success: false,
            error: 'Search limit exceeded. Redirecting to external search...',
            searchLimitExceeded: true
          };
        }
        
        return {
          success: false,
          error: error?.message || 'Search failed'
        };
      }

      console.log('✅ Search successful:', { resultCount: data?.length, searchParams: enhancedParams });

      // Process results and add search context
      const processedData = data?.map(record => ({
        ...record,
        searchContext: {
          matchedFields: this.getMatchedFields(record, enhancedParams),
          relevanceScore: this.calculateRelevanceScore(record, enhancedParams)
        }
      }));

      // FIX: Get total count with correct parameter names
      const { data: countData } = await supabase?.rpc('search_flex_count', {
        p_first: enhancedParams?.firstName || null,
        p_last: enhancedParams?.lastName || null,
        p_dob: enhancedParams?.dob || null,
        p_facility: enhancedParams?.facility || null,
        p_agency: enhancedParams?.agency || null,
        p_status: enhancedParams?.status || null,
        p_arrest_date_from: enhancedParams?.arrestDateFrom || null,
        p_arrest_date_to: enhancedParams?.arrestDateTo || null,
        p_release_date_from: enhancedParams?.releaseDateFrom || null,
        p_release_date_to: enhancedParams?.releaseDateTo || null,
        p_charge_q: enhancedParams?.chargeQuery || null,
        p_warrant_q: enhancedParams?.warrantQuery || null,
        p_city: enhancedParams?.city || null,
        p_zip: enhancedParams?.zip || null,
        p_booking_no: enhancedParams?.bookingNumber || null,
        p_case_no: enhancedParams?.caseNumber || null,
        p_fuzzy: enhancedParams?.fuzzy || false
      });

      const totalCount = countData || 0;
      const pageSize = enhancedParams?.pageSize || 25;
      const currentPage = enhancedParams?.page || 1;
      
      return {
        success: true,
        data: processedData || [],
        pagination: {
          page: currentPage,
          pageSize: pageSize,
          total: totalCount,
          totalPages: Math.ceil(totalCount / pageSize)
        },
        searchMetadata: {
          searchTerms: enhancedParams,
          originalQuery: searchParams?.query,
          fuzzySearch: enhancedParams?.fuzzy || false,
          totalMatches: totalCount
        }
      };
    } catch (error) {
      console.error('🚨 Search exception:', error);
      if (error?.message?.includes('Failed to fetch') || error?.message?.includes('NetworkError')) {
        return {
          success: false,
          error: 'Cannot connect to database. Please check your connection and try again.'
        };
      }
      
      return {
        success: false,
        error: error?.message || 'Search failed'
      };
    }
  };

  // Test database connection
  async testDatabaseConnection() {
    try {
      console.log('🔍 Testing database connection...');
      
      // Test 1: Basic database connectivity - FIX: Use proper count query
      const { count: personCount, error: testError } = await supabase
        ?.from('persons')
        ?.select('*', { count: 'exact', head: true });
        
      if (testError) {
        return {
          success: false,
          error: `Database connection failed: ${testError?.message}`,
          tests: { connectivity: false }
        };
      }

      // Test 2: Search function availability
      const { data: searchTest, error: searchError } = await supabase?.rpc('search_flex', {
        p_first: 'Jose',
        p_last: 'Jiminez',
        p_dob: null,
        p_facility: null,
        p_agency: null,
        p_status: null,
        p_arrest_date_from: null,
        p_arrest_date_to: null,
        p_release_date_from: null,
        p_release_date_to: null,
        p_charge_q: null,
        p_warrant_q: null,
        p_city: null,
        p_zip: null,
        p_booking_no: null,
        p_case_no: null,
        p_fuzzy: true,
        p_page: 1,
        p_page_size: 5
      });

      if (searchError) {
        return {
          success: false,
          error: `Search function error: ${searchError?.message}`,
          tests: { connectivity: true, searchFunction: false }
        };
      }

      // Test 3: Data existence
      const { data: dataCheck, error: dataError } = await supabase
        ?.from('persons')
        ?.select('person_id')
        ?.limit(1);
      
      return {
        success: true,
        tests: {
          connectivity: true,
          searchFunction: true,
          dataAvailable: (dataCheck?.length || 0) > 0,
          searchResults: (searchTest?.length || 0)
        },
        message: `Database operational. Found ${searchTest?.length || 0} search results for "Jose Jiminez". Total persons in database: ${personCount || 0}`
      };
    } catch (error) {
      console.error('🚨 Database test failed:', error);
      return {
        success: false,
        error: `Database test failed: ${error?.message}`,
        tests: { connectivity: false }
      };
    }
  }

  // Enhanced simple search method for general queries like "John"
  simpleSearch = async (query, options = {}) => {
    if (!query || query?.trim()?.length < 2) {
      return {
        success: false,
        error: 'Search term must be at least 2 characters long'
      };
    }

    const searchTerm = query?.trim();
    
    return this.flexibleSearch({
      query: searchTerm,
      firstName: searchTerm,
      lastName: searchTerm,
      fuzzy: true,
      ...options
    });
  };

  // Helper method to identify matched fields
  getMatchedFields = (record, searchParams) => {
    const matchedFields = [];
    
    if (searchParams?.firstName && record?.first_name?.toLowerCase()?.includes(searchParams?.firstName?.toLowerCase())) {
      matchedFields?.push('first_name');
    }
    if (searchParams?.lastName && record?.last_name?.toLowerCase()?.includes(searchParams?.lastName?.toLowerCase())) {
      matchedFields?.push('last_name');
    }
    if (searchParams?.dob && record?.date_of_birth === searchParams?.dob) {
      matchedFields?.push('date_of_birth');
    }
    if (searchParams?.facility && record?.facility_id === searchParams?.facility) {
      matchedFields?.push('facility');
    }
    if (searchParams?.agency && record?.agency_code === searchParams?.agency) {
      matchedFields?.push('agency');
    }
    if (searchParams?.status && record?.status === searchParams?.status) {
      matchedFields?.push('status');
    }
    
    return matchedFields;
  };

  // Helper method to calculate relevance score
  calculateRelevanceScore = (record, searchParams) => {
    let score = 0;
    
    // Exact name matches get higher scores
    if (searchParams?.firstName && record?.first_name?.toLowerCase() === searchParams?.firstName?.toLowerCase()) {
      score += 10;
    } else if (searchParams?.firstName && record?.first_name?.toLowerCase()?.includes(searchParams?.firstName?.toLowerCase())) {
      score += 5;
    }
    
    if (searchParams?.lastName && record?.last_name?.toLowerCase() === searchParams?.lastName?.toLowerCase()) {
      score += 10;
    } else if (searchParams?.lastName && record?.last_name?.toLowerCase()?.includes(searchParams?.lastName?.toLowerCase())) {
      score += 5;
    }
    
    // DOB exact match
    if (searchParams?.dob && record?.date_of_birth === searchParams?.dob) {
      score += 15;
    }
    
    // Recent arrests get slight boost
    if (record?.arrest_datetime) {
      const arrestDate = new Date(record?.arrest_datetime);
      const daysDiff = Math.floor((new Date() - arrestDate) / (1000 * 60 * 60 * 24));
      if (daysDiff < 30) score += 2;
      if (daysDiff < 7) score += 3;
    }
    
    return score;
  };

  // Keep existing searchRecords for backwards compatibility but mark as deprecated
  async searchRecords(params) {
    console.warn('searchRecords is deprecated. Use flexibleSearch instead.');
    return this.flexibleSearch(params);
  }

  // Get person history view data
  async getPersonHistory({ personId = null, limit = 100 }) {
    try {
      let query = supabase?.from('v_person_history')?.select('*')?.limit(limit)?.order('Record Uploaded At', { ascending: false });

      if (personId) {
        // Note: v_person_history doesn't expose person_id directly
        // This would require additional filtering logic
        query = query?.ilike('First Name', `%${personId}%`);
      }

      const { data, error } = await query;

      if (error) {
        return { success: false, error: error?.message };
      }

      return {
        success: true,
        data: data || []
      };
    } catch (error) {
      return { 
        success: false, 
        error: 'Failed to retrieve person history' 
      };
    }
  }

  // Get current population by facility
  async getCurrentPopulation() {
    try {
      const { data, error } = await supabase?.rpc('get_current_population_by_facility');

      if (error) {
        return { success: false, error: error?.message };
      }

      return {
        success: true,
        data: data || []
      };
    } catch (error) {
      return { 
        success: false, 
        error: 'Failed to retrieve population data' 
      };
    }
  }

  // Get facilities list
  async getFacilities() {
    try {
      const { data, error } = await supabase?.from('facilities')?.select('facility_id, facility_name')?.order('facility_name');

      if (error) {
        return { success: false, error: error?.message };
      }

      return {
        success: true,
        data: data || []
      };
    } catch (error) {
      return { 
        success: false, 
        error: 'Failed to retrieve facilities list' 
      };
    }
  }

  // Get agencies list
  async getAgencies() {
    try {
      const { data, error } = await supabase?.from('agencies')?.select('agency_code, agency_name')?.order('agency_name');

      if (error) {
        return { success: false, error: error?.message };
      }

      return {
        success: true,
        data: data || []
      };
    } catch (error) {
      return { 
        success: false, 
        error: 'Failed to retrieve agencies list' 
      };
    }
  }

  // Get detailed arrest record with charges and warrants
  async getArrestDetails(arrestId) {
    try {
      // Get basic arrest info
      const { data: arrestData, error: arrestError } = await supabase?.from('arrests')?.select(`*,person:persons!inner(*),facility:facilities(facility_name),agency:agencies(agency_name)`)?.eq('arrest_id', arrestId)?.single();

      if (arrestError) {
        return { success: false, error: arrestError?.message };
      }

      // Get charges
      const { data: charges, error: chargesError } = await supabase?.from('charges')?.select('*')?.eq('arrest_id', arrestId)?.order('seq');

      // Get warrants
      const { data: warrants, error: warrantsError } = await supabase?.from('warrants')?.select('*')?.eq('arrest_id', arrestId)?.order('seq');

      if (chargesError || warrantsError) {
        return { 
          success: false, 
          error: chargesError?.message || warrantsError?.message 
        };
      }

      return {
        success: true,
        data: {
          ...arrestData,
          charges: charges || [],
          warrants: warrants || []
        }
      };
    } catch (error) {
      return { 
        success: false, 
        error: 'Failed to retrieve arrest details' 
      };
    }
  }

  // Process CSV upload with comprehensive normalization
  async processCSVUpload(file, filename) {
    try {
      // Create upload batch record
      const { data: batchData, error: batchError } = await supabase?.from('upload_batches')?.insert([{
          filename,
          file_size: file?.size,
          status: 'processing',
          total_records: 0,
          processed_records: 0,
          duplicate_records: 0
        }])?.select()?.single();

      if (batchError) {
        return { success: false, error: batchError?.message };
      }

      // Process CSV file (placeholder - actual CSV parsing would go here)
      const processedRecords = await this.parseCSVFile(file, batchData?.id);
      
      // Update batch with results
      const { error: updateError } = await supabase?.from('upload_batches')?.update({
          status: 'completed',
          total_records: processedRecords?.total || 0,
          processed_records: processedRecords?.processed || 0,
          duplicate_records: processedRecords?.duplicates || 0,
          completed_at: new Date()?.toISOString()
        })?.eq('id', batchData?.id);

      if (updateError) {
        return { success: false, error: updateError?.message };
      }

      return {
        success: true,
        data: {
          batchId: batchData?.id,
          ...processedRecords
        }
      };
    } catch (error) {
      return { 
        success: false, 
        error: 'Failed to process CSV upload' 
      };
    }
  }

  // CSV parsing helper (simplified version)
  async parseCSVFile(file, batchId) {
    // This is a placeholder for actual CSV parsing logic
    // In a real implementation, this would:
    // 1. Parse CSV rows
    // 2. Normalize data using the normalize_arrest_data function
    // 3. Insert persons, arrests, charges, warrants
    // 4. Handle duplicates and identity hashing
    // 5. Return processing statistics
    
    return {
      total: 100,
      processed: 95,
      duplicates: 5
    };
  }

  // Export records to CSV
  async exportRecords(searchParams) {
    try {
      const records = await this.searchRecords({
        ...searchParams,
        pageSize: 10000 // Export all matching records
      });

      if (!records?.success) {
        return records;
      }

      // Convert to CSV format
      const csvData = this.convertToCSV(records?.data);
      
      return {
        success: true,
        data: csvData,
        filename: `inmate_records_${new Date()?.toISOString()?.slice(0, 10)}.csv`
      };
    } catch (error) {
      return { 
        success: false, 
        error: 'Failed to export records' 
      };
    }
  }

  // CSV conversion helper
  convertToCSV(records) {
    if (!records?.length) return '';

    const headers = [
      'First Name', 'Middle Name', 'Last Name', 'Date of Birth','Gender', 'Height (in)', 'Weight (lbs)', 'Eye Color', 'Hair Color','Race/Ethnicity', 'Arrest Date/Time', 'Arrest Location', 'Booking Date/Time', 'Arresting Agency', 'Detention Facility','Total Bail Amount', 'Status', 'Charges', 'Outstanding Warrants'
    ];

    const csvRows = [
      headers?.join(','),
      ...records?.map(record => [
        this.escapeCSV(record?.first_name || ''),
        this.escapeCSV(record?.middle_name || ''),
        this.escapeCSV(record?.last_name || ''),
        record?.date_of_birth || '',
        this.escapeCSV(record?.gender || ''),
        record?.height_inches || '',
        record?.weight_lbs || '',
        this.escapeCSV(record?.eye_color || ''),
        this.escapeCSV(record?.hair_color || ''),
        this.escapeCSV(record?.race_ethnicity || ''),
        record?.arrest_datetime || '',
        this.escapeCSV(record?.arrest_location || ''),
        record?.booking_datetime || '',
        this.escapeCSV(record?.agency_name || ''),
        this.escapeCSV(record?.facility_id || ''),
        record?.total_bail || '',
        record?.status || '',
        this.escapeCSV(record?.charges || ''),
        this.escapeCSV(record?.warrants || '')
      ]?.join(','))
    ];

    return csvRows?.join('\n');
  }

  escapeCSV(field) {
    if (field?.toString()?.includes(',') || field?.toString()?.includes('"') || field?.toString()?.includes('\n')) {
      return `"${field?.toString()?.replace(/"/g, '""')}"`;
    }
    return field?.toString() || '';
  }

  // Get dashboard statistics
  async getDashboardStats() {
    try {
      const { data, error } = await supabase?.rpc('get_sample_rows');

      if (error) {
        return { success: false, error: error?.message };
      }

      return {
        success: true,
        data: data || {}
      };
    } catch (error) {
      return { 
        success: false, 
        error: 'Failed to retrieve dashboard statistics' 
      };
    }
  }

  // Log search activity
  async logSearch(searchParams, resultCount) {
    try {
      const { error } = await supabase?.from('search_history')?.insert([{
          search_terms: searchParams,
          results_count: resultCount || 0
        }]);

      if (error) {
        // Non-critical error, don't fail the search
        console.warn('Failed to log search:', error?.message);
      }

      return { success: true };
    } catch (error) {
      // Non-critical error
      console.warn('Failed to log search:', error);
      return { success: true };
    }
  }

  // Log export activity
  async logExport(searchCriteria, recordCount, exportType = 'csv') {
    try {
      const { error } = await supabase?.from('export_history')?.insert([{
          export_type: exportType,
          search_criteria: searchCriteria,
          records_count: recordCount || 0
        }]);

      if (error) {
        console.warn('Failed to log export:', error?.message);
      }

      return { success: true };
    } catch (error) {
      console.warn('Failed to log export:', error);
      return { success: true };
    }
  }
}

// Create service instance
const inmateServiceInstance = new InmateService();

// Add this line to create the alias before exporting
const inmateService = inmateServiceInstance;

// Export both default and named export pointing to the same instance
export default inmateServiceInstance;
export { inmateService };